import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useOrganization } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  CurrentMerchantForVerifyDomainQuery,
  VerifyDomainMutation,
  VerifyDomainMutationVariables
} from '@/gql/graphql';
import { Logo } from '../../components/logos/logo';
import { ArrowLeft } from 'lucide-react';
import { getFavicon, urlToFile } from '@/utils/util';
import { ContextualAlert } from '@/components/contextual-alert';

const URL_REGEX = new RegExp(
  '^(https?:\\/\\/)?([a-zA-Z0-9-]+\\.)*[a-zA-Z0-9-]+\\.[a-zA-Z]{2,}(\\/.*)?$'
);

const formSchema = z.object({
  storeUrl: z
    .string()
    .refine((value) => URL_REGEX.test(value), { message: 'Please enter a valid URL with the www.' })
});

const CURRENT_MERCHANT_QUERY = gql`
  query CurrentMerchantForVerifyDomain {
    currentMerchant {
      isDomainVerified
      id
      websiteUrl
    }
  }
`;

const VERIFY_DOMAIN_MUTATION = gql`
  mutation VerifyDomain($websiteUrl: String!) {
    verifyDomain(websiteUrl: $websiteUrl) {
      id
      isDomainVerified
      websiteUrl
    }
  }
`;

export function VerifyDomain() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const [hasAttemptedVerification, setHasAttemptedVerification] = useState(false);
  const { data: currentMerchantData } =
    useQuery<CurrentMerchantForVerifyDomainQuery>(CURRENT_MERCHANT_QUERY);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      storeUrl: currentMerchantData?.currentMerchant.websiteUrl ?? ''
    },
    mode: 'onChange'
  });

  const [verifyDomain, { error: createCustomHostnameError, loading }] = useMutation<
    VerifyDomainMutation,
    VerifyDomainMutationVariables
  >(VERIFY_DOMAIN_MUTATION);

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setHasAttemptedVerification(true);
    void verifyDomain({
      variables: { websiteUrl: values.storeUrl }
    });
  };

  const updateLogo = async (staticImageUrl: string) => {
    if (organization) {
      try {
        const file = await urlToFile(staticImageUrl, 'logo.png');
        await organization.setLogo({ file });
      } catch (error) {
        console.error('Error updating logo:', error);
      }
    }
  };

  const handleContinue = async () => {
    try {
      const websiteUrl = currentMerchantData?.currentMerchant.websiteUrl;
      if (!websiteUrl) {
        return;
      }
      const favicon = await getFavicon(websiteUrl);
      if (favicon) {
        await updateLogo(favicon);
      }
    } catch (error) {
      console.error('Failed to fetch favicon:', error);
    }
    void navigate('../setup/dns-records');
  };

  return (
    <div className="flex items-center justify-center h-screen w-screen">
      <Card className="w-full max-w-xl mx-4 sm:mx-auto p-9 rounded-[1rem]">
        <ArrowLeft
          className="cursor-pointer"
          onClick={() => {
            void navigate(-1);
          }}
        />
        <CardHeader className="items-center space-y-8 p-0">
          <Logo icon="contextual" width={36} height={36} />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium">
            Connect your store
          </h1>
        </CardHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              void form.handleSubmit(onSubmit)(e);
            }}
          >
            <CardContent className="my-9 p-0">
              <div className="mb-9">
                <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold mb-4">
                  Enter your store URL
                </h2>
                <span className="text-xs sm:text-xs md:text-sm lg:text-base text-gray-600">{`Once you enter the domain, click on "Verify Domain". Our system will check if the domain is valid and create a first-party subdomain for the Contextual Tag.`}</span>
              </div>
              <FormField
                control={form.control}
                name="storeUrl"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel className="text-black">Store URL</FormLabel>
                    <FormControl>
                      <>
                        <div className="flex">
                          <span
                            className={`inline-flex items-center px-3 text-gray-500 border rounded-e-0 border-gray-300 border-e-0 rounded-s-md text-sm  ${createCustomHostnameError ? `border-redLight` : `border-gray-300`}`}
                          >
                            https://
                          </span>
                          <Input
                            {...field}
                            placeholder="www.lululemon.com"
                            className={`pr-20 rounded-e-md	border placeholder:text-gray-400 border-gray-300 rounded-l-none ${createCustomHostnameError ? `border-redLight` : `border-gray-300`}`}
                            disabled={currentMerchantData?.currentMerchant.isDomainVerified}
                          />
                        </div>
                      </>
                    </FormControl>
                    <>
                      {fieldState.error && (
                        <FormMessage className="text-red-700 text-xs">
                          {fieldState.error.message}
                        </FormMessage>
                      )}
                    </>
                  </FormItem>
                )}
              />
              {/* Alert Section - Only one will be shown based on priority */}
              {createCustomHostnameError ? (
                <ContextualAlert
                  className="mt-8"
                  variant="error"
                  message="We were unable to create a first party subdomain for your store. Please reach out to support@getcontextual.io for assistance."
                />
              ) : currentMerchantData?.currentMerchant.isDomainVerified ? (
                <ContextualAlert className="mt-8" variant="success" message="Domain Verified" />
              ) : (
                hasAttemptedVerification && (
                  <ContextualAlert
                    className="mt-8"
                    variant="error"
                    message="Your domain couldn't be verified. Please check the URL and try again."
                  />
                )
              )}
            </CardContent>

            <CardFooter className="p-0 justify-center space-x-6">
              {currentMerchantData?.currentMerchant.isDomainVerified ? (
                <Button onClick={() => void handleContinue()}>{`Save and Continue`}</Button>
              ) : (
                <Button type="submit" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify Domain'}
                </Button>
              )}

              <Button variant="secondary">Cancel</Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </div>
  );
}
